import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type { Competency, CompetencyRequest } from './competencyTypes';
import type { CompetencyOption } from '../personnel/personnelTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const competenciesApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<Competency>>(`/api/competencies${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<Competency>(`/api/competencies/${id}`, { signal }),

  /**
   * The ACTIVE competencies, in the framework's own order.
   *
   * What the Training/L&D form's picker is built from. Deleted rows are left out — the picker
   * asks what can be chosen now; a record already naming a retired competency keeps it, and the
   * panel still states it.
   */
  options: (signal?: AbortSignal) =>
    api<CompetencyOption[]>('/api/competencies/options', { signal }),

  /**
   * Adds a competency.
   *
   * **Idempotent on the name**, like the eligibility and family-relation creates and unlike the
   * survey question's: the Training/L&D form adds to this framework from inside its own picker,
   * so a reader who types one that already exists wanted that competency rather than an error
   * about it. The cost, stated rather than hidden: this catalog's own create modal no longer
   * reports a duplicate name — it returns the row holding it. An UPDATE still refuses one.
   */
  create: (body: CompetencyRequest) =>
    api<Competency>('/api/competencies', { method: 'POST', body }),

  update: (id: string, body: CompetencyRequest) =>
    api<Competency>(`/api/competencies/${id}`, { method: 'PUT', body }),

  /** Soft delete. L&D records naming it keep resolving its name. */
  remove: (id: string) => api<void>(`/api/competencies/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`/api/competencies/${id}/restore`, { method: 'POST' }),

  /** Soft-deletes every competency at once; resolves with how many. Records already naming one stay. */
  clear: () => api<number>('/api/competencies/clear', { method: 'POST' }),
};
